import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover } from '@mui/material';
import { SButton } from '@chargebee/sting-react';
import {
  clearPendingStructuralEditor,
  selectPendingStructuralEditor,
} from '@bbkAdminRedux/rtkq/designer-locale.slice';

import './generic-dialog.scss';

export type ModalOnCloseReason = 'backdropClick' | 'escapeKeyDown';
type Props = {
  disabled?: boolean;
  actionButton?: typeof DefaultActionButton;
  onOpen?: () => void;
  onClose?: (reason?: ModalOnCloseReason) => void;
  popoverProps?: Omit<React.ComponentProps<typeof Popover>, 'open'> & {
    open?: boolean;
  };
  /** Reopen when `pendingStructuralEditor` matches after a primary switch. */
  autoOpenKey?: string;
  children: (props: { handleClose: () => void }) => React.ReactNode;
};

export const DefaultActionButton: React.FC<{
  onClick: React.MouseEventHandler<HTMLElement>;
  disabled: boolean | undefined;
}> = (props) => (
  <SButton variant="primary-ghost" className="tw-p-1" {...props}>
    Open
  </SButton>
);

export const GenericPopover: React.FC<Props> = ({
  children,
  disabled,
  actionButton: ActionButton = DefaultActionButton,
  onOpen,
  onClose,
  popoverProps,
  autoOpenKey,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const pendingStructuralEditor = useSelector(selectPendingStructuralEditor);

  const handleOpen: React.MouseEventHandler<HTMLElement> = (event) => {
    onOpen?.();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (reason?: ModalOnCloseReason) => {
    onClose?.(reason);
    setAnchorEl(null);
  };

  useLayoutEffect(() => {
    if (!autoOpenKey || pendingStructuralEditor !== autoOpenKey) return;

    if (anchorEl) {
      dispatch(clearPendingStructuralEditor());
      return;
    }

    const trigger = triggerRef.current;
    if (!trigger) return;

    const anchor = (trigger.firstElementChild as HTMLElement | null) ?? trigger;
    onOpen?.();
    setAnchorEl(anchor);
    dispatch(clearPendingStructuralEditor());
  }, [autoOpenKey, pendingStructuralEditor, anchorEl, dispatch, onOpen]);

  return (
    <>
      <div ref={triggerRef}>
        <ActionButton onClick={handleOpen} disabled={disabled} />
      </div>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={(e, reason) => handleClose(reason)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        {...popoverProps}
      >
        {children({ handleClose })}
      </Popover>
    </>
  );
};
